import type { z } from "zod";
import { baseSchema } from "./schema";

export type StrongbackFlow = "tee" | "90" | "other";

export function createStrongbackSchema(flow: StrongbackFlow) {
  return baseSchema.superRefine((data, ctx) => {
    if (flow === "tee") {
      validateTeeFlow(data, ctx);
    } else if (flow === "90") {
      validate90Flow(data, ctx);
    } else {
      validateNonTeeFlow(data, ctx);
    }
  });
}

function validateTeeFlow(data: z.infer<typeof baseSchema>, ctx: z.RefinementCtx) {
  // Validate header and riser selections
  if (data.strongback_header === undefined) {
    ctx.addIssue({ code: "custom", path: ["strongback_header"], message: "Strongback header is required" });
  }
  if (data.strongback_riser === undefined) {
    ctx.addIssue({ code: "custom", path: ["strongback_riser"], message: "Strongback riser is required" });
  }

  // Validate strongback types and distances for header (strongbacks 1 and 2)
  if (data.strongback_header) {
    requireStrongbackType(data, ctx, 0);
    requireStrongbackType(data, ctx, 1);
    validateStrongback(data, ctx, 0);
    validateStrongback(data, ctx, 1);
  }

  // Validate strongback types and distances for riser (strongbacks 3 and 4)
  if (data.strongback_riser) {
    requireStrongbackType(data, ctx, 2);
    requireStrongbackType(data, ctx, 3);
    validateStrongback(data, ctx, 2);
    validateStrongback(data, ctx, 3);
  }
}

// 90 (long radius) flow: Top shows strongbacks 1 and 2, Bottom shows
// strongbacks 3 and 4. Strongbacks 2 and 4 are locked to Bar/0 in the UI, so
// they're always valid and don't need a "type is required" check.
function validate90Flow(data: z.infer<typeof baseSchema>, ctx: z.RefinementCtx) {
  if (data.strongback_top === undefined) {
    ctx.addIssue({ code: "custom", path: ["strongback_top"], message: "Strongback top is required" });
  }
  if (data.strongback_bottom === undefined) {
    ctx.addIssue({ code: "custom", path: ["strongback_bottom"], message: "Strongback bottom is required" });
  }

  if (data.strongback_top) {
    requireStrongbackType(data, ctx, 0);
    validateStrongback(data, ctx, 0);
    validateStrongback(data, ctx, 1);
  }

  if (data.strongback_bottom) {
    requireStrongbackType(data, ctx, 2);
    validateStrongback(data, ctx, 2);
    validateStrongback(data, ctx, 3);
  }
}

function validateNonTeeFlow(data: z.infer<typeof baseSchema>, ctx: z.RefinementCtx) {
  requireStrongbackType(data, ctx, 0);
  requireStrongbackType(data, ctx, 1);
  validateStrongback(data, ctx, 0);
  validateStrongback(data, ctx, 1);
}

function requireStrongbackType(data: z.infer<typeof baseSchema>, ctx: z.RefinementCtx, index: number) {
  const strongback = data.strongbacks[index];
  if (!strongback?.type) {
    ctx.addIssue({
      code: "custom",
      path: ["strongbacks", index, "type"],
      message: `Strongback ${index + 1} type is required`,
    });
  }
}

function validateStrongback(data: z.infer<typeof baseSchema>, ctx: z.RefinementCtx, index: number) {
  const strongback = data.strongbacks[index];
  if (!strongback?.type || strongback.type === "none") return;

  if (strongback.distance === undefined || Number.isNaN(strongback.distance)) {
    ctx.addIssue({ code: "custom", path: ["strongbacks", index, "distance"], message: "Distance is required" });
  }
  if (!strongback.unit_for_distance) {
    ctx.addIssue({ code: "custom", path: ["strongbacks", index, "unit_for_distance"], message: "Unit is required" });
  }

  if (strongback.type === "Loosering") {
    const flangeNps = strongback.flange_nps;
    const isCustomFlange = flangeNps === "custom";

    if (!flangeNps) {
      ctx.addIssue({ code: "custom", path: ["strongbacks", index, "flange_nps"], message: "Flange NPS is required" });
    }

    if (!isCustomFlange && !strongback.flange_rating) {
      ctx.addIssue({
        code: "custom",
        path: ["strongbacks", index, "flange_rating"],
        message: "Flange rating is required",
      });
    }

    if (isCustomFlange) {
      if (strongback.custom_diameter === undefined || Number.isNaN(strongback.custom_diameter)) {
        ctx.addIssue({
          code: "custom",
          path: ["strongbacks", index, "custom_diameter"],
          message: "Custom diameter is required",
        });
      }
      if (strongback.flange_num_bolt_holes === undefined || Number.isNaN(strongback.flange_num_bolt_holes)) {
        ctx.addIssue({
          code: "custom",
          path: ["strongbacks", index, "flange_num_bolt_holes"],
          message: "Number of bolt holes is required",
        });
      }
      if (strongback.flange_stud_size === undefined || Number.isNaN(strongback.flange_stud_size)) {
        ctx.addIssue({
          code: "custom",
          path: ["strongbacks", index, "flange_stud_size"],
          message: "Flange stud size is required",
        });
      }
      if (strongback.flange_bolt_circle === undefined || Number.isNaN(strongback.flange_bolt_circle)) {
        ctx.addIssue({
          code: "custom",
          path: ["strongbacks", index, "flange_bolt_circle"],
          message: "Flange bolt circle is required",
        });
      }
    }
  }
}
